import { OAuthMethodName, OAuthRequest, OAuthResponse, OAuthServiceName, OAuthServices } from "./oauth";
import { debounce, uuidv4 } from "./util";

window.name = window.name || uuidv4();

declare global {
    interface Window {
        applicationRoot: any;
    }
}

interface WebRequestInfo {
    ID: string;
    namespace: string;
    componentName: string;
    className: string;
    methodName: string;
    data: any;
    resolve: (data: any) => void;
    reject: (errMsg: string) => void;
}
let currentWebRequests: WebRequestInfo[] = [];

const sendWebRequest = debounce(async (requests: WebRequestInfo[]) => {
    currentWebRequests = [];

    const baseUrl = import.meta.env.DEV ? '/api/v1/components' : 'api/v1/components';
    let rootURL = ``;

    if (window.applicationRoot) {
        rootURL = `${location.origin}${window.applicationRoot}`;
    }

    // If there's only one request, send it directly to the endpoint to aid in troubleshooting
    if (requests.length === 1) {
        const req = requests[0];
        const response = await fetch(`${rootURL}${baseUrl}/${req.namespace}/${req.componentName}/${req.className}/${req.methodName}`, {
            body: JSON.stringify(req.data),
            headers: {
                'Content-Type': 'application/json',
                'WINDOW_ID': window.name,
            },
            method: 'POST',
        });

        const contentType = response.headers.get('Content-Type');
        await processResponse(req, contentType,
            () => response.json(),
            () => response.text());
    }
    else {
        const response = await fetch(`${rootURL}${baseUrl}/batch`, {
            body: JSON.stringify(requests),
            headers: {
                'Content-Type': 'application/json',
                'WINDOW_ID': window.name,
            },
            method: 'POST',
        });

        const json = await response.json();
        for (const res of json.data) {
            const req = requests.find(r => r.ID == res.ID);
            if (!req) {
                throw new Error(`Unable to find matching request data for ${res.ID}`);
            }

            processResponse(req, res.contentType,
                () => Promise.resolve(res),
                () => Promise.resolve(res.data));
        }
    }
}, 50);

async function processResponse(req: WebRequestInfo, contentType: string | null, fetchJson: () => Promise<any>, fetchXml: () => Promise<string>) {
    if (contentType?.includes('application/json')) {
        const json = await fetchJson();
        if (json.error) {
            const errMsg = json.error?.message || json.error;
            req.reject(errMsg);
        }
        else {
            req.resolve(json.data);
        }
    } else if (contentType?.includes('application/xml') || contentType?.includes('text/xml')) {
        req.resolve(await fetchXml());
    } else {
        req.reject('Unexpected content-type: ' + contentType);
    }
}

export function call(namespace: string, componentName: string, className: string, methodName: string, data: any) {
    if (data && typeof data === 'object') {
        for (var i in data) {
            if (typeof data[i] === 'object') {
                data[i] = JSON.stringify(data[i]);
            }
        }
    }

    return new Promise<any>((resolve, reject) => {
        const req = {
            ID: uuidv4(),
            namespace,
            componentName,
            className,
            methodName,
            data,
            resolve,
            reject
        };
        currentWebRequests.push(req);
        sendWebRequest(currentWebRequests);
    });
}

export async function callOAuthService<T extends OAuthServiceName, K extends OAuthMethodName<T>>(serviceName: T, methodName: K, args: OAuthRequest<T, K>): Promise<OAuthResponse<T, K>> {
    if (window.ST?.CallOAuthService) {
        return new Promise((resolve, reject) => {
            const jqDeferred = window.ST.CallOAuthService(serviceName, methodName, args, {});
            jqDeferred.done((data: any) => resolve(data)).fail((err: any) => reject(err));
        })

    } else {
        throw new Error('Not implemented');
    }
}
